import PropTypes from "prop-types"
import React, { createContext, useEffect, useState } from "react"
import { useQuery } from "react-query"

import { updateUrlQueryParams } from "src/utils/url/parsing/marina"

import { queryContractQuotesByGroup } from "../../../api/Contracts"
import Loader from "../../../components/Loader"
import { useToast } from "../../../hooks/use_toast"
import ContractGroupTabLayout from "./ContractGroupTabLayout"
import Contracts from "./Contracts"

export const ContractGroupContext = createContext(null)

const ContractGroup = ({
  archivedGroupsPagePath,
  attachments,
  canUpdate,
  group,
  marinaName,
  namedRates,
  selectedContractId,
  templates,
  templatesPath,
}) => {
  const showToast = useToast()
  const [initialTabSet, setInitialTabSet] = useState(false)

  const { data, isError, isLoading } = useQuery(
    ["contractGroups", group.encodedId, "contractQuotes"],
    () => queryContractQuotesByGroup({ groupId: group.encodedId }),
    { retry: false }
  )

  const totalCount = data
    ? data.completed.length + data.outstanding.length + data.unsent.length
    : 0

  const [currentTab, setCurrentTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get("tab") || "unsent"
  })

  useEffect(() => {
    if (!data) return
    if (initialTabSet) return

    setInitialTabSet(true)

    if (totalCount === 0) {
      setCurrentTab("unsent")
    } else if (
      data.unsent.length > 0 &&
      (currentTab !== "outstanding" || currentTab !== "completed")
    ) {
      setCurrentTab("unsent")
    } else if (data.outstanding.length > 0 && currentTab !== "completed") {
      setCurrentTab("outstanding")
    } else if (data.completed.length > 0) {
      setCurrentTab("completed")
    }
  }, [currentTab, data, initialTabSet, totalCount])

  useEffect(() => {
    updateUrlQueryParams({
      selected_contract_id: null,
      tab: currentTab,
    })
  }, [currentTab])

  useEffect(() => {
    if (selectedContractId) {
      showToast(
        "Your contract quote has been submitted for processing and will be ready to send once it is fully processed.",
        { type: "success" }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderContracts = () => {
    if (isLoading) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <Loader name="contracts" />
        </div>
      )
    }

    if (isError) {
      return (
        <div className="text-red-600">
          There was an error loading your contracts. Please reload the page to
          try again.
        </div>
      )
    }

    return <Contracts />
  }

  return (
    <ContractGroupContext.Provider
      value={{
        archivedGroupsPagePath,
        attachments,
        canUpdate,
        contracts: data,
        currentTab,
        group,
        marinaName,
        namedRates,
        setCurrentTab,
        templates,
        templatesPath,
        totalCount,
      }}
    >
      <div className="px-6">
        <div className="grid grid-cols-12 gap-3 pb-36">
          <div className="col-span-12 flex flex-col self-start border bg-white xl:col-span-5">
            <ContractGroupTabLayout />
          </div>
          <div className="col-span-12 xl:col-span-7">{renderContracts()}</div>
        </div>
      </div>
    </ContractGroupContext.Provider>
  )
}

ContractGroup.propTypes = {
  archivedGroupsPagePath: PropTypes.string.isRequired,
  attachments: PropTypes.array,
  canUpdate: PropTypes.bool.isRequired,
  group: PropTypes.object.isRequired,
  namedRates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      amount: PropTypes.number,
      taxRate: PropTypes.number,
    })
  ).isRequired,
  marinaName: PropTypes.string.isRequired,
  selectedContractId: PropTypes.string,
  templates: PropTypes.array.isRequired,
  templatesPath: PropTypes.string.isRequired,
}

export default ContractGroup
